// BEUS-11496 M3: `/v2/heatmap/summary`(includeBreakdown) 응답 -> 기존 dream-api `/es/get_basic_info`·`/es/get_hour_basic_info`
// 응답 모양(es_response_helper.reform_basic_info_response). 화면의 metric·기기 선택·referrer 목록·시간대 그래프를 그대로 쓰기 위한 변환이다.
//
// 기존 응답:
//   pv: Visit 문서 수(hits.total)
//   device: { [device]: { pv, newPv, returnPv, refer: [{ refer, pv, newPv, returnPv }], sessTime, newSessTime, returnSessTime } }
//     - Visit이 있는 기기만 key가 있다(ES terms bucket).
//     - refer: ES terms(refer, missing 'Direct', size 10) -> 문서 수 내림차순, 같으면 key 오름차순, 상위 10개.
//       referrer가 없는 Visit과 값이 'Direct'인 Visit은 ES에서 같은 'Direct' bucket이 된다.
//     - sessTime: avg(sessTime). newSessTime·returnSessTime은 그 방문자 유형 Visit이 없으면 기기 sessTime 그대로다.
//   hour: (get_basic_info이고 startDate === endDate일 때만) [[pv, 'HH'], ...] 첫 Visit 시각부터 마지막 Visit 시각까지
//         한 시간씩(사이 빈 시간은 0), 그 뒤를 [0, 'notyet']로 채워 24개. 시각은 apply timezone offset 기준.
//   uv는 기존 응답에도 없다(화면 metric.uv는 0). 그래서 v2 UV는 _v2 아래에만 둔다.

import { toV2Filters, toV2Range, unsupportedReason } from './request'

// v2는 Direct(referrer 없음)와 값 'Direct'를 나눠 준다. 기존처럼 합친 뒤 상위 10개를 고르려면 그보다 넉넉히 받아야 한다.
export const REFERRER_FETCH_LIMIT = 100
const LEGACY_REFERRER_LIMIT = 10

function hasHour (params) {
  return params.hour !== undefined && params.hour !== null && params.hour !== ''
}

// hourly: 기존 get_basic_info의 time_type 규칙(요청에 hour가 없고 startDate === endDate). get_hour_basic_info는 항상 false.
export function toV2SummaryRequest (params, tzOffset = '+09:00', { hourly = false } = {}) {
  const reason = unsupportedReason(params)
  if (reason) return { supported: false, reason }
  return {
    supported: true,
    body: {
      aid: params.aid,
      range: toV2Range(params, tzOffset),
      granularity: hourly ? 'hour' : 'day',
      // Legacy basic/hour-basic info keeps the base population; frequency is applied by event reads.
      filters: toV2Filters({ ...params, freq: null }),
      includeBreakdown: true,
      referrerLimit: REFERRER_FETCH_LIMIT
    }
  }
}

export function isHourlyBasicInfo (params) {
  return !hasHour(params) && params.startDate === params.endDate
}

function offsetMinutes (tzOffset) {
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec(tzOffset || '')
  if (!m) return 9 * 60
  const sign = m[1] === '-' ? -1 : 1
  return sign * (Number(m[2]) * 60 + Number(m[3]))
}

function localHour (iso, tzOffset) {
  const t = new Date(new Date(iso).getTime() + offsetMinutes(tzOffset) * 60000)
  return t.getUTCHours()
}

// ES terms 순서: 문서 수 내림차순, 같으면 key 오름차순.
function compareRefer (a, b) {
  if (b.pv !== a.pv) return b.pv - a.pv
  if (a.refer < b.refer) return -1
  if (a.refer > b.refer) return 1
  return 0
}

function legacyRefers (items) {
  const merged = new Map()
  for (const item of items) {
    const key = item.type === 'direct' ? 'Direct' : item.value
    const prev = merged.get(key) || { refer: key, pv: 0, newPv: 0, returnPv: 0 }
    prev.pv += item.pageViews
    prev.newPv += item.newPageViews
    prev.returnPv += item.returnPageViews
    merged.set(key, prev)
  }
  return [...merged.values()].sort(compareRefer).slice(0, LEGACY_REFERRER_LIMIT)
}

function legacyHours (series, tzOffset) {
  const byHour = new Map()
  for (const bucket of series || []) {
    if (!bucket.pageViews) continue
    const h = localHour(bucket.startAt, tzOffset)
    byHour.set(h, (byHour.get(h) || 0) + bucket.pageViews)
  }
  const hour = []
  if (byHour.size) {
    const hours = [...byHour.keys()]
    for (let h = Math.min(...hours); h <= Math.max(...hours); h++) {
      hour.push([byHour.get(h) || 0, String(h).padStart(2, '0')])
    }
  }
  while (hour.length < 24) hour.push([0, 'notyet'])
  return hour
}

export function toLegacyBasicInfo (result, { hourly = false, tzOffset = '+09:00' } = {}) {
  const device = {}
  let truncatedReferrers = false
  // 기존 device key 순서 = ES terms bucket 순서(문서 수 내림차순, 같으면 key 오름차순). 기본 기기 고르기 등이 이 순서를 볼 수 있다.
  const byDevice = [...((result.breakdown && result.breakdown.byDevice) || [])]
    .sort((a, b) => (b.pageViews - a.pageViews) || (a.device < b.device ? -1 : a.device > b.device ? 1 : 0))
  for (const d of byDevice) {
    if (!d.pageViews) continue
    const duration = d.sessionDuration || {}
    const average = duration.averageMs === undefined ? null : duration.averageMs
    if (d.referrers.truncated) truncatedReferrers = true
    device[d.device] = {
      pv: d.pageViews,
      newPv: d.newPageViews,
      returnPv: d.returnPageViews,
      refer: legacyRefers(d.referrers.items),
      sessTime: average,
      newSessTime: d.newPageViews ? (duration.newAverageMs === undefined ? null : duration.newAverageMs) : average,
      returnSessTime: d.returnPageViews ? (duration.returnAverageMs === undefined ? null : duration.returnAverageMs) : average
    }
  }
  const out = { pv: result.totals.pageViews, device }
  if (hourly) out.hour = legacyHours(result.series, tzOffset)
  // 화면은 _v2를 읽지 않는다(비교·console 확인용).
  out._v2 = {
    dataVersion: result.dataVersion,
    watermarkAt: result.watermarkAt,
    uniqueVisitors: result.totals.uniqueVisitors,
    sessions: result.totals.sessions,
    // 기기별 referrer가 REFERRER_FETCH_LIMIT보다 많으면 Direct 합치기 뒤 상위 10개가 기존과 다를 수 있다.
    truncatedReferrers
  }
  return out
}

// `/v2/heatmap/scroll`의 avgViewportHeightPx -> 기존 getAvgFoldData 결과({ avgFold } 또는 null).
// 기존: dream-api `/es/get_average_fold`가 기기별 avg(vph)(Visit, vph < 10000)를 주고, 화면 API 함수가 그 기기 값이
// 참(0·null 아님)일 때만 { avgFold }를 돌려준다.
export function toLegacyAvgFold (scrollResult) {
  const value = scrollResult && scrollResult.avgViewportHeightPx
  return value ? { avgFold: value } : null
}
